/**
 * Issue Popover Component (Web)
 *
 * Desktop hover popover showing issue details, suggestion,
 * and accept/ignore actions.
 */

import React, { useState, useMemo, useRef, useCallback } from 'react';
import { View, Text, Pressable, ScrollView, Platform, Modal } from 'react-native';
import { useTranslation } from '@dramadetector/shared';
import type { IssuePopoverProps } from './types';
import { styles, getSeverityStyles } from './inlineAnnotationsStyles';

const POPOVER_MAX_WIDTH = 480; // Wider for better readability
const POPOVER_MAX_HEIGHT = '90dvh'; // Dynamic viewport height (accounts for mobile browser chrome)

/**
 * IssuePopover - Web hover popover (bottom sheet)
 */
export function IssuePopover({
  issue,
  isVisible,
  anchorPosition,
  onAccept,
  onIgnore,
  onClose,
  onMouseEnter,
  onMouseLeave,
  testID,
  // Navigation props
  issues,
  currentIndex,
  onPrevious,
  onNext,
}: IssuePopoverProps) {
  const { t } = useTranslation('features');
  const [showExplanation, setShowExplanation] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [shouldRender, setShouldRender] = useState(isVisible);
  const severityStyles = getSeverityStyles(issue.severity);
  const animationFrameRef = React.useRef<number | null>(null);
  const unmountTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);
  const bottomSheetRef = useRef<View>(null);

  // Animation lifecycle with requestAnimationFrame + onTransitionEnd
  React.useEffect(() => {
    if (isVisible) {
      // Mount immediately
      setShouldRender(true);

      // Use requestAnimationFrame for reliable animation trigger (2 frames for reflow)
      animationFrameRef.current = requestAnimationFrame(() => {
        animationFrameRef.current = requestAnimationFrame(() => {
          setIsAnimating(true);
        });
      });
    } else {
      // Animate out
      setIsAnimating(false);

      // Fallback timeout in case onTransitionEnd doesn't fire
      unmountTimeoutRef.current = setTimeout(() => {
        unmountTimeoutRef.current = null;
        setShouldRender(false);
      }, 400); // 400ms fallback (300ms animation + 100ms buffer)
    }

    // Cleanup on unmount or when isVisible changes
    return () => {
      if (animationFrameRef.current !== null) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      if (unmountTimeoutRef.current !== null) {
        clearTimeout(unmountTimeoutRef.current);
      }
    };
  }, [isVisible]);

  // Handle transition end (more reliable than timeout for unmount)
  const handleTransitionEnd = React.useCallback(() => {
    if (!isVisible) {
      if (unmountTimeoutRef.current) {
        clearTimeout(unmountTimeoutRef.current);
        unmountTimeoutRef.current = null;
      }
      setShouldRender(false);
    }
  }, [isVisible]);

  /**
   * Smart backdrop dismissal - only close if tap is outside bottom sheet
   * Preserves backdrop dismiss without blocking scroll inside sheet
   */
  const handleBackdropPress = useCallback(
    (event: any) => {
      if (!bottomSheetRef.current) {
        onClose();
        return;
      }

      // Web-specific: Use pointer coordinates to detect outside tap
      if (Platform.OS === 'web' && event.nativeEvent) {
        const { clientY } = event.nativeEvent;

        // Get bottom sheet bounds
        // @ts-ignore - React Native Web provides measure on web
        bottomSheetRef.current.measure((x, y, width, height, pageX, pageY) => {
          // Check if tap is above bottom sheet (in backdrop area)
          if (clientY < pageY) {
            onClose();
          }
          // If tap is inside bottom sheet bounds, do nothing (allow scroll)
        });
      } else {
        // Fallback: close on backdrop tap (native handles this differently)
        onClose();
      }
    },
    [onClose]
  );

  if (!shouldRender) {
    return null;
  }

  return (
    <Modal
      visible={isVisible}
      transparent
      animationType="none" // We handle animation ourselves
      onRequestClose={onClose}
      statusBarTranslucent // Allow overlay to cover status bar
    >
      {/* Full-screen overlay */}
      <Pressable
        style={styles.popoverOverlay}
        onPress={handleBackdropPress}
        testID={`${testID}-backdrop`}
      >
        {/* Bottom Sheet (slides up from bottom) */}
        <View
          ref={bottomSheetRef}
          style={[
            styles.popoverBottomSheet,
            isAnimating
              ? styles.popoverBottomSheetVisible
              : styles.popoverBottomSheetHidden,
          ]}
          testID={testID}
          onStartShouldSetResponder={() => true} // Prevent click-through
          {...(Platform.OS === 'web' && {
            onTransitionEnd: handleTransitionEnd,
          })}
        >
        <ScrollView style={styles.popoverScrollView} contentContainerStyle={styles.popoverContent}>
          {/* Navigation (if multiple issues) */}
          {issues && issues.length > 1 && currentIndex !== undefined && (
            <View style={styles.bottomSheetNav}>
              <Pressable
                style={[
                  styles.bottomSheetNavButton,
                  currentIndex === 0 && styles.bottomSheetNavDisabled,
                ]}
                onPress={currentIndex > 0 ? onPrevious : undefined}
                disabled={currentIndex === 0}
                accessibilityLabel={t('inline_annotations.previous', 'Previous')}
                testID={`${testID}-previous`}
              >
                <Text style={{ fontSize: 18, color: currentIndex > 0 ? '#1F2937' : '#D1D5DB' }}>
                  ←
                </Text>
              </Pressable>

              <Text style={styles.bottomSheetNavText}>
                {currentIndex + 1} / {issues.length}
              </Text>

              <Pressable
                style={[
                  styles.bottomSheetNavButton,
                  currentIndex === issues.length - 1 && styles.bottomSheetNavDisabled,
                ]}
                onPress={currentIndex < issues.length - 1 ? onNext : undefined}
                disabled={currentIndex === issues.length - 1}
                accessibilityLabel={t('inline_annotations.next', 'Next')}
                testID={`${testID}-next`}
              >
                <Text style={{ fontSize: 18, color: currentIndex < issues.length - 1 ? '#1F2937' : '#D1D5DB' }}>
                  →
                </Text>
              </Pressable>
            </View>
          )}

          {/* SECTION 1: ISSUE ANALYSIS (Always Visible - Read-Only) */}
          <View style={styles.issueAnalysisSection}>
            {/* Header */}
            <View style={styles.popoverHeader}>
              <View style={[styles.popoverSeverityBadge, severityStyles.badge]}>
                <Text style={[styles.popoverSeverityText, { color: '#FFFFFF' }]}>
                  {issue.severity}
                </Text>
              </View>
              <Text style={styles.popoverTitle} numberOfLines={1}>
                {issue.title}
              </Text>
              <Pressable
                style={styles.popoverCloseButton}
                onPress={onClose}
                accessibilityLabel={t('common.close', 'Close')}
              >
                <Text style={{ fontSize: 18, color: '#9CA3AF' }}>×</Text>
              </Pressable>
            </View>

            {/* Snippet */}
            <View style={styles.popoverSnippet}>
              <Text style={styles.popoverSnippetText}>"{issue.snippet}"</Text>
            </View>

            {/* Description (why it's problematic) */}
            {issue.suggestion && (
              <View style={styles.popoverDescription}>
                <Text style={styles.popoverDescriptionText}>{issue.suggestion}</Text>
              </View>
            )}
          </View>

          {/* SECTION 2: SUGGESTION (Conditionally Visible) */}
          {issue.replacement ? (
            <View style={styles.suggestionSection}>
              {/* Section Divider */}
              <View style={styles.sectionDivider} />

              {/* Rewrite Preview - "Try instead" */}
              <View style={styles.rewriteSection}>
                <View style={styles.rewriteLabel}>
                  <Text style={styles.rewriteLabelText}>
                    💡 {t('inline_annotations.try_instead', 'Try instead')}
                  </Text>
                </View>
                <View style={styles.rewriteContent}>
                  <Text style={styles.rewriteText}>"{issue.replacement}"</Text>
                </View>
              </View>

              {/* Drama Reduction Badge */}
              <View style={styles.popoverDramaReduction}>
                <View style={styles.dramaReductionBadge}>
                  <Text style={styles.dramaReductionText}>
                    🎯 -{issue.dramaReduction}% {t('inline_annotations.drama', 'drama')}
                  </Text>
                </View>
              </View>

              {/* Explanation Accordion - moved from analysis section */}
              {issue.explanation.length > 0 && (
                <View style={styles.explanationContainer}>
                  <Pressable
                    style={styles.explanationHeader}
                    onPress={() => setShowExplanation(!showExplanation)}
                    accessibilityRole="button"
                    aria-expanded={showExplanation}
                    aria-controls="explanation-content"
                  >
                    <Text style={styles.explanationTitle}>
                      {t('inline_annotations.why_this_matters', 'Why this matters')}
                    </Text>
                    <Text style={{ color: '#9CA3AF' }}>
                      {showExplanation ? '▲' : '▼'}
                    </Text>
                  </Pressable>

                  {showExplanation && (
                    <View style={styles.explanationList} id="explanation-content">
                      {issue.explanation.map((item, index) => (
                        <View key={index} style={styles.explanationItem}>
                          <View style={styles.explanationBullet} />
                          <Text style={styles.explanationText}>{item}</Text>
                        </View>
                      ))}
                    </View>
                  )}
                </View>
              )}

              {/* Action Buttons */}
              {issue.status === 'pending' && (
                <View style={styles.actionButtonsRow}>
                  <Pressable
                    style={styles.acceptButton}
                    onPress={onAccept}
                    accessibilityRole="button"
                    accessibilityLabel={t('inline_annotations.accept', 'Accept')}
                    testID={`${testID}-accept`}
                  >
                    <Text style={styles.acceptButtonText}>
                      {t('inline_annotations.accept', 'Accept')}
                    </Text>
                  </Pressable>
                  <Pressable
                    style={styles.ignoreButton}
                    onPress={onIgnore}
                    accessibilityRole="button"
                    accessibilityLabel={t('inline_annotations.ignore', 'Ignore')}
                    testID={`${testID}-ignore`}
                  >
                    <Text style={styles.ignoreButtonText}>
                      {t('inline_annotations.ignore', 'Ignore')}
                    </Text>
                  </Pressable>
                </View>
              )}

              {/* Status indicator if not pending */}
              {issue.status !== 'pending' && (
                <View
                  style={[
                    styles.actionButtonsRow,
                    { justifyContent: 'center' },
                  ]}
                >
                  <Text
                    style={{
                      fontSize: 14,
                      color: issue.status === 'accepted' ? '#10B981' : '#6B7280',
                      fontWeight: '600',
                    }}
                  >
                    {issue.status === 'accepted'
                      ? t('inline_annotations.accepted', '✓ Accepted')
                      : t('inline_annotations.ignored', 'Ignored')}
                  </Text>
                </View>
              )}
            </View>
          ) : (
            <View style={styles.suggestionSection}>
              {/* Section Divider */}
              <View style={styles.sectionDivider} />

              {/* No Suggestion Available Banner */}
              <View style={styles.noSuggestionBanner}>
                <Text style={styles.noSuggestionIcon}>ℹ️</Text>
                <View style={styles.noSuggestionContent}>
                  <Text style={styles.noSuggestionTitle}>
                    {t('inline_annotations.no_suggestion', 'No suggestion available for this issue')}
                  </Text>
                  <Text style={styles.noSuggestionText}>
                    {t('inline_annotations.manual_revision', 'This pattern requires manual revision based on your specific context.')}
                  </Text>
                </View>
              </View>
            </View>
          )}
        </ScrollView>
        </View>
      </Pressable>
    </Modal>
  );
}

export default IssuePopover;
